/** @format */

import { Grid } from '@material-ui/core';
import { useEffect, useState, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import Breadcrumb from 'src/components/Breadcrumb';
import Page from 'src/components/Page';
import useQuery from 'src/hooks/useQuery';
import {
  clearKnowledgeBasetError,
  getKnowledgeBase,
  getKnowledgeBaseDetail,
  rateKnowledgeBaseDetail
} from 'src/slices/knowledgeBase';
import { useDispatch, useSelector } from 'src/store';
import { ContentBlock } from '../legal-pages/contentBlock';
import { Redirect } from 'react-router';
import { KnowledgeBaseRating } from 'src/components/knowledegeBase/rating';
import { KnowledgeBaseDetailContentSkeleton, KnowledgeBaseDetailSkeleton } from 'src/components/knowledegeBase/item';

const startViewTransition = callback => {
  if (document.startViewTransition) {
    document.startViewTransition(() => {
      flushSync(callback);
    });
  } else {
    callback();
  }
};

export default function KnowledgeBaseDetail() {
  const dispatch = useDispatch();
  const { nid, kb } = useParams();
  const { error, globalLoading } = useSelector(state => state.global);
  const {
    knowledgeBaseDetail,
    knowledgeBaseDetailLoading,
    knowledgeBase,
    error: knowledgeBaseError,
    ratingSuccess,
    ratingError
  } = useSelector(state => state.knowledgeBase);

  const [displayedContent, setDisplayedContent] = useState({ loading: true, detail: null });
  const prevLoadingRef = useRef(true);

  useEffect(() => {
    dispatch(getKnowledgeBase(kb));
  }, [dispatch, kb]);

  useEffect(() => {
    if (!nid) return;
    dispatch(getKnowledgeBaseDetail(nid, false));
  }, [nid, dispatch]);

  const isLoading = globalLoading || knowledgeBaseDetailLoading || !knowledgeBase;

  useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = isLoading;
    if (wasLoading && !isLoading && knowledgeBaseDetail) {
      startViewTransition(() => {
        setDisplayedContent({ loading: false, detail: knowledgeBaseDetail });
      });
    } else if (isLoading && !wasLoading) {
      const titlePreview = knowledgeBaseDetail?.title != null ? { title: knowledgeBaseDetail.title } : null;
      setDisplayedContent({ loading: true, detail: titlePreview });
    } else if (!isLoading && knowledgeBaseDetail) {
      setDisplayedContent({ loading: false, detail: knowledgeBaseDetail });
    } else if (isLoading && knowledgeBaseDetail?.title != null) {
      setDisplayedContent(prev => ({ ...prev, detail: { title: knowledgeBaseDetail.title } }));
    }
  }, [nid, isLoading, knowledgeBaseDetail]);

  const { title: pageTitle, rating, ratingCount, categories } = displayedContent.detail || {};

  if (error || knowledgeBaseError || !nid) return <Redirect to="/404" />;
  return (
    <Page className={`knowledge-base`} title={pageTitle}>
      <Breadcrumb
        data={[
          { title: knowledgeBase?.title || '', link: `/knowledge-base/${kb}` },
          { title: displayedContent.detail?.title || '' }
        ]}
        additionClassName="container-narrow"
      />
      <Grid className="container" style={{ viewTransitionName: 'kb-detail' }}>
        {displayedContent.loading ? (
          <Grid className="content-container container container-narrow">
            <KnowledgeBaseDetailSkeleton />
          </Grid>
        ) : (
          <>
            <Grid className="content-container container container-narrow">
              <Grid className="knowledge-base-title-box">
                <h1 className="knowledge-base-title">{pageTitle}</h1>
              </Grid>
              <Grid className="knowledge-base-rating">
                <KnowledgeBaseRating rating={rating} />
                {rating && (
                  <Grid className="knowledge-base-rating-count">
                    {rating.toFixed(1)} | {ratingCount} Ratings
                  </Grid>
                )}
              </Grid>
              {categories && (
                <Grid className="knowledge-base-content-categories">
                  {categories.map(cat => (
                    <Grid key={cat} className="knowledge-base-content-categories-item">
                      {cat}
                    </Grid>
                  ))}
                </Grid>
              )}
            </Grid>
            <Grid className="knowledge-base-content content-container container container-narrow">
              {displayedContent.detail && (
                <ContentBlock
                  nid={nid}
                  kb={kb}
                  content={displayedContent.detail}
                  rating={rating}
                  clearError={clearKnowledgeBasetError}
                  rateDetail={rateKnowledgeBaseDetail}
                  loading={knowledgeBaseDetailLoading}
                  ratingError={ratingError}
                  ratingSuccess={ratingSuccess}
                />
              )}
            </Grid>
          </>
        )}
      </Grid>
    </Page>
  );
}
